import React, {useState} from 'react'
import './todos.less'
import {useIntl} from 'react-intl'
import {ToDo} from '../../entities/todoList'
import {Card, Popconfirm, Tag, message} from 'antd'
import {DeleteOutlined, EditOutlined, QuestionCircleOutlined} from '@ant-design/icons'
import {instance as toDoService} from '../../services/ToDoService'
import {Link, useNavigate, useRevalidator, useSubmit} from 'react-router-dom'
import {CustomJournalEventType, getToDoTypeInfo} from '../../entities/journalRecord'
import {FinishToDoButton} from './FinishToDoButton'
import {markPlantForRevalidation} from '../../routes/plantRevalidator'
import {markToDoForRevalidation} from '../../routes/todoRevalidator'
import {instance as journalService} from '../../services/JournalService'
import {EmEmoji} from '../emoji/EmEmoji'

export type ToDoCardProps = {
  todo: ToDo
  userJournalEventTypes: CustomJournalEventType[]
}

export function ToDoCard(props: ToDoCardProps) {
  const intl = useIntl()
  const [isLoading, setLoading] = useState(false)
  const submit = useSubmit()
  const [completionDate, setCompletionDate] = useState(new Date())

  const navigate = useNavigate()

  const revalidator = useRevalidator()

  const actions: React.ReactNode[] = [
    <EditOutlined key="edit" onClick={() => navigate(`/todos/${props.todo.id}`)} />,

    <Popconfirm
      placement="bottom"
      title={intl.formatMessage({id: 'areYouSureYouWantToDelete'})}
      description={intl.formatMessage({id: 'thisOperationIsIrreversible'})}
      icon={<QuestionCircleOutlined style={{color: 'red'}} />}
      onConfirm={async () => {
        setLoading(true)
        const result = await toDoService.deleteToDo(props.todo.id)
        setLoading(false)
        if (result.success) {
          submit(null, {method: 'post'})
        } else {
          message.warning(intl.formatMessage({id: 'failedToDeleteTask'}))
        }
      }}
      okText={intl.formatMessage({id: 'yes'})}
      cancelText={intl.formatMessage({id: 'no'})}
    >
      <DeleteOutlined disabled={isLoading} key="delete" onClick={(e) => e.stopPropagation()} />
    </Popconfirm>,

    <FinishToDoButton
      userJournalEventTypes={props.userJournalEventTypes}
      finishedTodo={props.todo}
      isText={false}
    />,
  ]

  const eventTypeInfo = getToDoTypeInfo(props.todo.type)

  return (
    <div className="todo__list__cards__card">
      <Card actions={actions} hoverable className="todo__list__cards__card__item">
        <Card.Meta
          // avatar={<Avatar src="https://api.dicebear.com/7.x/miniavs/svg?seed=1" />}
          avatar={
            eventTypeInfo ? (
              <EmEmoji id={eventTypeInfo.icon} size="1em"></EmEmoji>
            ) : (
              <QuestionCircleOutlined />
            )
          }
          title={
            <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap'}}>
              {props.todo.type && (
                <Link style={{color: 'black', textWrap: 'initial'}} to={`/todos/${props.todo.id}`}>
                  {props.todo.type.type === 'custom' || !props.todo.type.type
                    ? eventTypeInfo.name
                    : intl.formatMessage({id: `eventType-${props.todo.type.type}`})}
                </Link>
              )}
              {props.todo.importance && (
                <Tag color={props.todo.importance === 'urgent' ? 'red' : 'green'}>
                  {intl.formatMessage({id: props.todo.importance})}
                </Tag>
              )}
            </div>
          }
          description={
            <div className="todo__list__cards__card__description">
              {props.todo.plants && (
                <p>
                  {props.todo.plants.map((plant, index) => (
                    <Link to={`/todos/${props.todo.id}/plants/${plant.id}`} key={index}>
                      {index !== 0 ? ', ' : ''}
                      {plant.name}{' '}
                    </Link>
                  ))}
                </p>
              )}

              <div style={{display: 'flex'}}>
                {props.todo.targetStartDateUtc && <p>{props.todo.targetStartDateUtc}</p>}
                {props.todo.targetEndDateUtc && <p> - {props.todo.targetEndDateUtc}</p>}
              </div>
              {props.todo.comment && <p>{props.todo.comment}</p>}
            </div>
          }
        />
      </Card>
    </div>
  )
}
